import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import { useLexicon } from '../../hooks/useLexicon';
import React, { useMemo, useState } from 'react';
import { updateClassSubjects } from '../../service';
import { useApiSubjects } from '../../hooks/useSubjects';

const AU_YEAR_OPTIONS = ['7', '8', '9', '10', '11', '12'];
const US_YEAR_OPTIONS = ['6', '7', '8', '9', '10', '11', '12'];
// NZ spans Years 7-13: juniors (Yr7-10, NZ Curriculum) + NCEA (Yr11-13).
const NZ_YEAR_OPTIONS = ['7', '8', '9', '10', '11', '12', '13'];

const subjectMatchesYears = (subject, selectedYears) => {
  const subjectYears = subject.years || [];
  if (!subjectYears.length || !selectedYears.length) return true;
  return selectedYears.some((year) =>
    subjectYears.some((sy) => Number(sy) === Number(year))
  );
};

/**
 * Blocking subject/year picker for LTI-provisioned classes (LMS courses carry no subject).
 *
 * Shown to an embedded (Canvas-launched) teacher whose classes lack subjects or years —
 * Jeddle needs both to drive curriculum-aligned marking, so the modal cannot be dismissed
 * until every listed class has at least one year and one subject. Saves through
 * PATCH /classes/{classId}/subjects, which writes the LearnDash group meta via WP and keeps
 * the LTI context link in step so roster syncs re-assert the choice.
 */
const ClassSubjectsModal = ({ classes, onSaved, onDismiss }) => {
  const { apiSubjects } = useApiSubjects();
  const { t, isUS, isNZ, yearLabel } = useLexicon();
  const yearOptions = isNZ
    ? NZ_YEAR_OPTIONS
    : isUS
    ? US_YEAR_OPTIONS
    : AU_YEAR_OPTIONS;
  const [selections, setSelections] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const selectionFor = (classId) =>
    selections[classId] || { years: [], subjectCodes: [] };

  const setSelection = (classId, patch) =>
    setSelections((prev) => ({
      ...prev,
      [classId]: { ...selectionFor(classId), ...patch },
    }));

  const subjectOptionsByClass = useMemo(() => {
    const options = {};
    (classes || []).forEach((cls) => {
      const years = selectionFor(cls.id).years;
      options[cls.id] = (apiSubjects || [])
        .filter((s) => subjectMatchesYears(s, years))
        .map((s) => ({
          code: s.subjectCode || s.code,
          title: s.subjectName || s.name || s.title,
        }))
        .filter((s) => s.code)
        .sort((a, b) => a.title.localeCompare(b.title));
    });
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes, apiSubjects, selections]);

  const allComplete = (classes || []).every((cls) => {
    const sel = selectionFor(cls.id);
    return sel.years.length > 0 && sel.subjectCodes.length > 0;
  });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        classes.map((cls) => {
          const sel = selectionFor(cls.id);
          return updateClassSubjects(cls.id, {
            subjectCodes: sel.subjectCodes,
            years: sel.years,
            // NZ classes carry the single "NZ" curriculum-state sentinel so the
            // server persists country/state provenance and tool lookup resolves
            // to NZ (never the AU getOrElse("NSW") fallback). AU/US send no
            // state key — byte-identical to today.
            ...(isNZ ? { state: 'NZ', states: ['NZ'] } : {}),
          });
        })
      );
      await onSaved?.();
    } catch (e) {
      setError('Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={!onDismiss}
      onClose={() => onDismiss?.()}
    >
      <DialogTitle>Set up your classes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {onDismiss
            ? t(
                'Pick the year level(s) and subject(s) for each class so marking and feedback are aligned to the right curriculum.'
              )
            : t(
                "Your learning platform doesn't tell Jeddle what each class teaches. Pick the year level(s) and subject(s) for each class so marking and feedback are aligned to the right curriculum."
              )}
        </Typography>
        {(classes || []).map((cls) => {
          const sel = selectionFor(cls.id);
          const subjectOptions = subjectOptionsByClass[cls.id] || [];
          return (
            <Box key={cls.id} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                {cls.title}
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel id={`years-${cls.id}`}>{t('Year level(s)')}</InputLabel>
                <Select
                  labelId={`years-${cls.id}`}
                  multiple
                  value={sel.years}
                  onChange={(e) =>
                    setSelection(cls.id, { years: e.target.value })
                  }
                  input={<OutlinedInput label={t('Year level(s)')} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((y) => (
                        <Chip key={y} label={yearLabel(y)} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {yearLabel(year)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel id={`subjects-${cls.id}`}>Subject(s)</InputLabel>
                <Select
                  labelId={`subjects-${cls.id}`}
                  multiple
                  value={sel.subjectCodes}
                  onChange={(e) =>
                    setSelection(cls.id, { subjectCodes: e.target.value })
                  }
                  input={<OutlinedInput label="Subject(s)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((code) => (
                        <Chip
                          key={code}
                          size="small"
                          label={
                            subjectOptions.find((s) => s.code === code)
                              ?.title || code
                          }
                        />
                      ))}
                    </Box>
                  )}
                >
                  {subjectOptions.map((subject) => (
                    <MenuItem key={subject.code} value={subject.code}>
                      {subject.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          );
        })}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {onDismiss && (
          <Button onClick={() => onDismiss()} disabled={saving}>
            Later
          </Button>
        )}
        <Button
          variant="contained"
          onClick={save}
          disabled={!allComplete || saving}
        >
          {saving ? 'Saving…' : 'Save and continue'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClassSubjectsModal;
